// TODO: only hightlight publish and send if an activity has been added and filled out

import { connect } from 'react-redux';
import { useState, useEffect, useRef, useMemo, useCallback, useLayoutEffect, } from 'react';
import { format } from 'date-fns';

// local imports
import { keyCountInObject, objectValuesToList, uuidv4, } from '../Util';
import { 
  Timestamp, DateStringMMDDYYYY, SET_LAST_VIEWED_PROJECT, FETCH_SCHEDULE_PDF, SET_SCHEDULE_MESSAGES_DIALOGUE_ID, EDIT_SCHEDULE, ADD_ACTIVITY, EDIT_ACTIVITY, DELETE_ACTIVITY,
  GET_SCHEDULE_MESSAGE_THREADS, GET_SCHEDULE, SiteboardApiValidator, ADD_SCHEDULE, DELETE_SCHEDULE, PUBLISH_SCHEDULE, UNPUBLISH_SCHEDULE, UUID, SET_ALERT, SET_LOADING,
  Activity, EDIT_PROJECT, SET_SHARE_LINK_DIALOGUE_SHOWING, SET_EDIT_SCHEDULE_DIALOGUE_SHOWING, SET_REQUEST_SCHEDULE_EMAIL_UPDATES_DIALOGUE_OPEN,
  GET_CONTACT_SHARED_SCHEDULE_STATS, SET_CONFIRM_SHARE_SCHEDULE_WORK_DATES_DIALOGUE_OPEN, SET_REVIEW_SHARED_SCHEDULE_VIEWS_DIALOGUE_OPEN,
  SET_REVIEW_SHARED_SCHEDULE_CONFIRMATIONS_DIALOGUE_OPEN,
} from '../actions/types';
import { 
  setPath, setDeleteScheduleDialogueId, setPublishScheduleDialogueId, setUnpublishScheduleDialogueId, setNewScheduleDialogueId, siteboardAction,
} from '../actions/defaultActions';

// component imports
import PoweredByBar from './PoweredByBar';
import DarkModeToggle from './DarkModeToggle';
import AlertCountBubble from './AlertCountBubble';
import Tooltip from './Tooltip';
import PushButton from './PushButton';
import CollapsibleContainer from './CollapsibleContainer';
import UserAvatar from './UserAvatar';

// icon imports
import { Hash, MapPin, Ellipsis, Send, CircleCheck, Eye, } from 'lucide-react';
import Logo from '../SiteBoard_Logo';
import AddIcon from '../icons/addIcon';
import RainIcon from '../icons/rainIcon';
import BackIcon from '../icons/backIcon';
import TrashIcon from '../icons/trashIcon';
import GantChartIcon from '../icons/gantChartIcon';
import UnlockIcon from '../icons/unlockIcon';
import HolidayIcon from '../icons/holidayIcon';
import ExpandDownIcon from '../icons/expandDownIcon';
import SortIcon from '../icons/sortIcon';
import CriticalIcon from '../icons/criticalIcon';
import CommentIcon from '../icons/commentIcon';
import RefreshIcon from '../icons/refreshIcon';
import LocationIcon from '../icons/locationIcon';
import PdfIcon from '../icons/pdfIcon';
import DownloadIcon from '../icons/downloadIcon';
import LinkIcon from '../icons/linkIcon';
import CalendarIcon from '../icons/calendarIcon';
import EmailIcon from '../icons/emailIcon';
import CircleCheckIcon from '../icons/circleCheckIcon';
import EyeIcon from '../icons/eyeIcon';

const bgStyle = 'bg-[#fff]';
const bgStyleDarkMode = 'bg-[#1e1e1e]';
const bgStyleDarkModeSelected = 'bg-neutral-700';
const textStyle = 'text-[#000]';
const textStyleDarkMode = 'text-[#fff]';
const fgColor = '#000';
const fgColorDarkMode = '#fff';
const cellBorderColor = 'border-[#000]';
const cellBorderLeftColor = 'border-[#888]';
const cellBorderColorRightDarkMode = 'border-[#555]';
const cellBorderColorDarkMode = 'border-[#555]';
const cellWidth = 'grow basis-0 min-w-[1.6em] h-full overflow-hidden';
const rowHeight = 'h-[1.7em] sm:h-[1.8em]';
const leftTableWidth = 'min-w-[280px] w-[50%]';
const rightTableWidth = 'grow';
const responsibleColWidth = 'w-[40%]';
const descriptionColWidth = 'w-[60%]';
const oneDayInMillis = 1000*60*60*24;
const scheduleLockedWarning = 'Schedule is locked to prevent accidental modification. Click "Published" then "Unlock" to modify the schedule.';

const testingRequestEmailUpdates = false;

export const Schedule = ({ 
  projectId,
  scheduleId,
  _projects,
  _schedules,
  _activities,
  _subscriptions,
  _users,
  _userId,
  _userPreferences,
  _unreadScheduleMessageThreads,
  _scheduleMessageThreads,
  _isTouchDevice,
  setPath=()=>{},
  setPublishScheduleDialogueId=()=>{},
  setUnpublishScheduleDialogueId=()=>{},
  setDeleteScheduleDialogueId=()=>{},
  setNewScheduleDialogueId=()=>{},
  siteboardAction=()=>{},
  _localOnlyMode,
  _isUsingShareScheduleLink,
  _contactSharedScheduleStats,
  sharedScheduleIsConfirmed,
  sharedScheduleCode = '',
  sharedScheduleContactEmail = '',
  sharedScheduleContactFirstName = '',
  sharedScheduleContactLastName = '',
  sharedScheduleContactCompanyName = '',
}) => {
  const [selectedActivityId, setSelectedActivityId] = useState(null);
  const [newActivityId, setNewActivityId] = useState(null);
  const [movedActivityId, setMovedActivityId] = useState(null);
  const [loadScheduleFailureMessage, setLoadScheduleFailureMessage] = useState(null);
  const [isPreparingPdf, setIsPreparingPdf] = useState(false);
  const [datesJson, setDatesJson] = useState('[]');
  const [publishStatsShowing, setPublishStatsShowing] = useState(true);
  const [currentDatestring, setCurrentDatestring] = useState(format(new Date(), 'MM/dd/yyyy'));
  const [activityListAtTop, setActivityListAtTop] = useState(true); 
  const [activityListAtButtom, setActivityListAtButtom] = useState(true); 

  const activityContainer = useRef(null);
  const mainContainer = useRef(null);
  const scheduleActivitiesRef = useRef([]);
  const descriptionInputLastFocused = useRef(false);
  const pdfGeneratorLoaded = useRef(false);
  const pdfGenerator = useRef({});
  const enterPressed = useRef(false);
  const arrowUpPressed = useRef(false);
  const arrowDownPressed = useRef(false);
  const selectAboveRowTimeout = useRef(false);
  const selectBelowRowTimeout = useRef(false);
  const oldScheduleOverlayRef = useRef(null);
  const selectedActivityIdRef = useRef(null);

  const onNewRowId = 'onNewRowId';
  const onMoveRowUpId = 'onMoveRowUp';
  const onMoveRowDownId = 'onMoveRowDown';
  const onDeleteRowId = 'onDeleteRow';

  const selectedProject = projectId in _projects ? _projects[projectId] : null;
  const selectedSubscription = selectedProject !== null ? _subscriptions[selectedProject.subscriptionId] : null;
  const selectedSchedule = (() => {
    if(scheduleId in _schedules) {
      // create a copy of selectedSchedule so we can alter it without affecting redux state. TODO: create a method in Schedule class to make deep copies
      return {
        ..._schedules[scheduleId], 
        weatherDates: {..._schedules[scheduleId].weatherDates},
        holidayDates: {..._schedules[scheduleId].holidayDates},
      };
    }
    return null;
  })();
  const isDarkMode = _userPreferences.darkMode === true;
  const latestScheduleDateString = (() => {
    if(!selectedProject) return null;
    const scheduleDates = objectValuesToList(selectedProject.schedules).map((dateString) => DateStringMMDDYYYY.toDateObject(dateString).getTime());
    if(scheduleDates.length === 0) return null;
    scheduleDates.sort((one, two) => { return two - one; });
    return format(new Date(scheduleDates[0]), 'MM/dd/yyyy');
  })();
  const isLatestSchedule = selectedSchedule ? selectedSchedule.beginDate === latestScheduleDateString : false;

  const getPdfGenerator = async () => {
    if(pdfGeneratorLoaded.current !== true) {
      pdfGeneratorLoaded.current = true;
      pdfGenerator.current = await import('../pdfGenerator/index.mjs');
    }
    return pdfGenerator.current;
  };

  useEffect(() => {
    if(_localOnlyMode !== true) return;
    if(!selectedProject) return;
    document.title = `${selectedProject.name} 3-Week`;
  }, [_localOnlyMode, selectedProject]);

  // Fetch schedule from server. If we already have the schedule this will do nothing
  useEffect(() => {
    if(_localOnlyMode) return;

    if(UUID.isValid(scheduleId) && UUID.isValid(projectId)) {
      const onSuccess = () => {};
      const onFailure = (failMessage) => { setLoadScheduleFailureMessage(failMessage); };
      siteboardAction(GET_SCHEDULE, { projectId: projectId, scheduleId: scheduleId}, onSuccess, onFailure);
      siteboardAction(GET_SCHEDULE_MESSAGE_THREADS, { projectId: projectId, }); // Preemptively fetch schedule threads from server
      siteboardAction(GET_CONTACT_SHARED_SCHEDULE_STATS, { projectId: projectId, scheduleId: scheduleId, });
    }
    //if(UUID.isValid(projectId)) {
    //  siteboardAction(SET_LAST_VIEWED_PROJECT, {projectId: projectId});
    //}
  }, [siteboardAction, projectId, scheduleId, selectedProject, selectedSchedule, _localOnlyMode]);

  // Deselect activity if mouse is clicked anywhere outside it. TODO: do this for touch devices too
  useEffect(() => {
    const onMouseDown = (event) => {
     try {
       let element = event.target;
       do {
         if(typeof element.className === 'string' && element.className.indexOf('activityLine') >= 0) return;
         if(typeof element.className === 'string' && element.className.indexOf('rowActionButton') >= 0) return;
         if(element.id === onNewRowId) return;
         if(element.id === onMoveRowUpId) return;
         if(element.id === onMoveRowDownId) return;
         if(element.id === onDeleteRowId) return;
         element = element.parentNode;
       } while(element.tagName !== 'body' && element.tagName !== 'BODY');
       setSelectedActivityId(null);
     } catch(error) {}
    };

    window.addEventListener('mousedown', onMouseDown);
    return () => { // remove listener when component unloads
      window.removeEventListener('mousedown', onMouseDown);
    };
  }, []);

  // Set the position for the OLD SCHEDULE warning
  useEffect(() => {
    function update() {
      if (!oldScheduleOverlayRef.current || !mainContainer.current) return;

      // Visible viewport rect
      const viewport = {
        top: 0,
        left: 0,
        right: window.innerWidth,
        bottom: window.innerHeight,
      };

      // mainContainer rect
      const r2 = mainContainer.current.getBoundingClientRect();

      // Intersection
      const top = Math.max(viewport.top, r2.top);
      const left = Math.max(viewport.left, r2.left);
      const right = Math.min(viewport.right, r2.right);
      const bottom = Math.min(viewport.bottom, r2.bottom);

      const width = right - left;
      const height = bottom - top;

      // Apply absolute positioning
      const el = oldScheduleOverlayRef.current;
      el.style.position = "fixed"; // fixed since we're using viewport coords
      el.style.top = `${top}px`;
      el.style.left = `${left}px`;
      el.style.width = `${right-left}px`;
      el.style.height = `${bottom-top}px`;
    }

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update);

    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update);
    };
  });

  const getDates = (selectedSchedule) => {
    if(!selectedSchedule) return [];

    // Create an array of datestrings for 21 days (3 weeks) from start date. These will contain static info about the days we will display
    const dates = []; // array of date timestrings (mm/dd/yyyy) that will be represented in this schedule
    const monthTwoLetterAbbreviations = {
      'Jan': 'Ja',
      'Feb': 'Fe',
      'Mar': 'Mr',
      'Apr': 'Ap',
      'May': 'My',
      'Jun': 'Jn',
      'Jul': 'Jl',
      'Aug': 'Au',
      'Sep': 'Se',
      'Oct': 'Oc', 
      'Nov': 'Nv', 
      'Dec': 'De',
    };
    try {
      const beginDateObject = DateStringMMDDYYYY.toDateObject(selectedSchedule.beginDate); // TODO: display an error if beginDateObject isn't valid?
      for(let i = 0; i < 21; i++) {
        const item = {
          monthString: format(beginDateObject, 'MMM'),// ie Jan, Feb, Mar
          monthShortString: monthTwoLetterAbbreviations[format(beginDateObject, 'MMM')],// ie JA, FE, MR
          dayOfWeekString: format(beginDateObject, 'ccc'), // ie Mon, Tue, Wed
          dayOfWeekShortString: format(beginDateObject, 'cccccc'), // ie Mo, Tu, We
          dayOfMonth: format(beginDateObject, 'dd'),
          dayOfMonthShort: format(beginDateObject, 'd'),
          isWeekend: beginDateObject.getDay() === 0 || beginDateObject.getDay() === 6, 
          dateString: format(beginDateObject, 'MM/dd/yyyy'),
          dateObject: new Date(beginDateObject.getTime()),
        }
        dates.push(item);
        beginDateObject.setTime(beginDateObject.getTime() + oneDayInMillis);
      }

      return dates;
    } catch(error) {}
    return [];
  };

  useEffect(() => {
    setDatesJson(JSON.stringify(getDates(selectedSchedule)));
  }, [selectedSchedule]);

  const addActivity = useCallback(() => {
    if(!selectedProject || !selectedSchedule) return;
    const onSuccess = (output) => {
      if(output.isTemporary === true) {
        setNewActivityId(output.activity.id);
      } else {
        if(!scheduleActivitiesRef.current) return;

        // don't select it if this isn't the most recently created activity
        let newestActivity = null;
        for(const activity of scheduleActivitiesRef.current) {
          if(newestActivity === null || activity.createdTimestamp > newestActivity.createdTimestamp) {
            newestActivity = activity;
          }
        }
        if(newestActivity !== null) {
          setNewActivityId(newestActivity.id);
        }
      }
    };

    document.activeElement.blur();
    setSelectedActivityId(null);
    descriptionInputLastFocused.current = false;

    siteboardAction(ADD_ACTIVITY, {
      subscriptionId: selectedProject.subscriptionId,
      activity: {
        id: uuidv4(),
        scheduleId: selectedSchedule.id,
        projectId: selectedProject.id,
        delayNotes: '',
        activeDates: {},
        createdTimestamp: new Date().getTime(),
        modifiedTimestamp: new Date().getTime(),
      }
    }, onSuccess); 
  }, [selectedProject, selectedSchedule, siteboardAction]);

  useEffect(() => {
    selectedActivityIdRef.current = selectedActivityId;
  }, [selectedActivityId]);

  // Global keyup and keydown handler, for selecting rows with arrow up/down keys, adding new row with enter key
  useEffect(() => {
    const selectAboveRow = () => {
      if(!scheduleActivitiesRef.current || scheduleActivitiesRef.current.length === 0) return;

      const selectedIndex = scheduleActivitiesRef.current.findIndex((activity) => activity.id === selectedActivityIdRef.current);
      if(selectedIndex > 0) {
        setNewActivityId(scheduleActivitiesRef.current[selectedIndex-1].id);
      } 
    };

    const selectBelowRow = () => {
      if(!scheduleActivitiesRef.current || scheduleActivitiesRef.current.length === 0) return;

      const selectedIndex = scheduleActivitiesRef.current.findIndex((activity) => activity.id === selectedActivityIdRef.current);
      if(selectedIndex < scheduleActivitiesRef.current.length-1) {
        setNewActivityId(scheduleActivitiesRef.current[selectedIndex+1].id);
      } 
    };

    const onUpArrow = (timeout) => {
      if(arrowUpPressed.current === false) return;
      selectAboveRow(); 
      clearTimeout(selectAboveRowTimeout.current);
      selectAboveRowTimeout.current = setTimeout(() => {
        onUpArrow();
      }, typeof timeout === 'number' ? timeout : 40);
    };

    const onDownArrow = (timeout) => {
      if(arrowDownPressed.current === false) return;
      selectBelowRow(); 
      clearTimeout(selectBelowRowTimeout.current);
      selectBelowRowTimeout.current = setTimeout(() => {
        onDownArrow();
      }, typeof timeout === 'number' ? timeout : 40);
    };

    const onKeyDown = (event) => {
      if(!selectedActivityIdRef.current) return;

      if(event.key === 'ArrowUp' && !arrowUpPressed.current) {
        try { 
          event.preventDefault();
          arrowUpPressed.current = true;
          onUpArrow(400);
        } catch(error) {}
      } else if(event.key === 'ArrowDown' && !arrowDownPressed.current) {
        try { 
          event.preventDefault();
          arrowDownPressed.current = true;
          onDownArrow(400);
        } catch(error) {}
      } else if(event.key === 'Enter' && !enterPressed.current) {
        try { 
          event.preventDefault();
          enterPressed.current = true;
          addActivity();
        } catch(error) {}
      }
    };

    const onKeyUp = (event) => {
      if(event.key === 'Enter') {
        enterPressed.current = false;
      } else if(event.key === 'ArrowUp') {
        arrowUpPressed.current = false;
        clearTimeout(selectAboveRowTimeout.current);
      } else if(event.key === 'ArrowDown') {
        arrowDownPressed.current = false;
        clearTimeout(selectBelowRowTimeout.current);
      }
    };

    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('keydown', onKeyDown);
    return () => { 
      window.removeEventListener('keyup', onKeyUp); 
      window.removeEventListener('keydown', onKeyDown); 
    }; // remove listener when component unloads
  }, [addActivity, selectedActivityIdRef]);

  // Refresh current day timestamp periodically to refresh current date indicator
  useEffect(() => {
    const updateInterval = setInterval(() => {
      setCurrentDatestring(format(new Date(), 'MM/dd/yyyy'));
    }, 30000);
    return () => { 
      clearInterval(updateInterval); // remove listener when component unloads
    };
  }, []);

  // Add a "more content below/above" gradient effect to the top and bottom of the scrollable activities area
  const initialScrollTopBottomIndicatorsSet =  useRef(false);
  const updateScrollTopBottomIndicators = () => {
    const element = activityContainer.current;
    if(!element) return;

    const isScrollable = element.scrollHeight > element.clientHeight;
    setActivityListAtTop(element.scrollTop === 0);
    setActivityListAtButtom(!isScrollable || (element.scrollTop + element.clientHeight) >= (element.scrollHeight - 5));
    initialScrollTopBottomIndicatorsSet.current = true;
  };
  useLayoutEffect(() => {
    const updateInterval = setInterval(() => {
      updateScrollTopBottomIndicators();
      if(initialScrollTopBottomIndicatorsSet.current === true) {
        clearInterval(updateInterval);
      }
    }, 200);
    updateScrollTopBottomIndicators();
    return () => { 
      clearInterval(updateInterval); // remove listener when component unloads
    };
  }, [scheduleId]);

  const userCanAddSchedule = () => {
    if(_localOnlyMode === true) return false;
    return SiteboardApiValidator[ADD_SCHEDULE].isUserIdAuthorized(_userId, selectedSubscription, selectedProject);
  };
  const userCanEditSchedule = () => {
    if (selectedSchedule === null) return false;
    if (selectedSchedule.isPublished === true) return false;
    return SiteboardApiValidator[EDIT_SCHEDULE].isUserIdAuthorized(_userId, selectedSubscription, selectedProject);
  };
  const userCanDeleteSchedule = () => {
    if (_localOnlyMode === true) return false;
    if (selectedSchedule === null) return false;
    if (selectedSchedule.isPublished === true) return false;
    return SiteboardApiValidator[DELETE_SCHEDULE].isUserIdAuthorized(_userId, selectedSubscription, selectedProject);
  };
  const userCanPublishSchedule = () => {
    if(_localOnlyMode) return false;
    if (selectedSchedule === null) return false;
    if (selectedSchedule.isPublished === true) return false;
    return SiteboardApiValidator[PUBLISH_SCHEDULE].isUserIdAuthorized(_userId, selectedSubscription, selectedProject);
  };
  const userCanUnpublishSchedule = () => {
    if(_localOnlyMode === true) return;
    if (selectedSchedule === null) return false;
    if (selectedSchedule.isPublished === false) return false;
    return SiteboardApiValidator[UNPUBLISH_SCHEDULE].isUserIdAuthorized(_userId, selectedSubscription, selectedProject);
  };
  const userCanViewGeneralNotes = () => {
    return SiteboardApiValidator[EDIT_SCHEDULE].isUserIdAuthorized(_userId, selectedSubscription, selectedProject);
  };

  const monthCellsJSX = useMemo(() => {
    const dates = JSON.parse(datesJson);
    return (
      <div className='flex w-full'>
        <div className={`${leftTableWidth}`}></div>
        <div className={`flex ${rowHeight} ${rightTableWidth}`}>
          {dates.map((date, index) => (
            <div key={index} className={`relative flex items-center justify-center ${cellWidth} border ${isDarkMode ? cellBorderColorRightDarkMode : cellBorderColor} border-r-0 border-b-0 last:border-r`}>
              <p className='text-[0.8em] 2xl:text-[0.9em] absolute top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%]  rotate-[-45deg]'>{date.monthString}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }, [datesJson, isDarkMode]);
  
  const daysOfWeekJSX = useMemo(() => {
    const dates = JSON.parse(datesJson);
    return (
      <div className={`flex ${rowHeight} ${rightTableWidth}`}>
        {dates.map((date, index) => (
          <div 
            key={index} 
            className={`relative flex items-center justify-center ${isDarkMode ? 'text-black bg-[#eee]' : 'text-white bg-[#a59e9e]'} ${cellWidth} border ${isDarkMode ? cellBorderColorDarkMode : cellBorderColor} border-r-0 border-b-0 last:border-r`}
          >
            <p className='absolute top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%] 2xl:hidden'>{date.dayOfWeekShortString}</p>
            <p className='absolute top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%] hidden 2xl:block'>{date.dayOfWeekString}</p>
          </div>
        ))}
      </div>
    );
  }, [datesJson, isDarkMode]);

  const dayOfMonthJSX = useMemo(() => {
    const dates = JSON.parse(datesJson);
    return (
      <div className={`flex ${rowHeight} ${rightTableWidth}`}>
        {dates.map((date, index) => (
          <div 
            key={index} 
            className={`${date.dateString === currentDatestring ? 'relative' : ''} flex items-center justify-center ${cellWidth} border ${isDarkMode ? cellBorderColorRightDarkMode : cellBorderColor} border-r-0 last:border-r ${isDarkMode ? (date.dateString === currentDatestring ? 'font-bold text-white ' : 'text-white') : (date.dateString === currentDatestring ? 'font-bold text-black' : 'bg-[#ece5e5] text-[#4483aa]')}`}
          >
            <p>{date.dayOfMonthShort}</p>
            {date.dateString === currentDatestring &&
              <>
                <div className={`absolute top-0 left-0 w-full h-full opacity-40 bg-yellow-500`}></div>
                {/*<div className={`absolute top-0 left-0 w-full h-full outline outline-[1px] outline-offset-[-1px] outline-yellow-600`}></div>*/}
              </>
            }
          </div>
        ))}
      </div>
    );
  }, [datesJson, isDarkMode, currentDatestring]);

  const saveBase64Pdf = (pdfBase64) => {
    // have the browser download the base64 pdf from the server
    // convert base64 to binary
    const binaryString = atob(pdfBase64);
    const binaryLength = binaryString.length;
    const bytes = new Uint8Array(binaryLength);
    for(let i = 0; i < binaryLength; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);

    // create a download link and click it
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = (selectedProject.name + ' ' + format(DateStringMMDDYYYY.toDateObject(selectedSchedule.beginDate), 'MM-dd-yyyy') + ' 3-week.pdf');
    document.body.appendChild(link);
    link.click();

    // cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  };

  const viewBase64Pdf = (pdfBase64) => {
    // have the browser download the base64 pdf from the server
    // convert base64 to binary
    const binaryString = atob(pdfBase64);
    const binaryLength = binaryString.length;
    const bytes = new Uint8Array(binaryLength);
    for(let i = 0; i < binaryLength; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);

    // Open PDF in a new tab
    window.open(blobUrl, "_blank");
  };

  const downloadPdf = () => {
    if(selectedSchedule === null) return;

    siteboardAction(FETCH_SCHEDULE_PDF, {
      scheduleId: selectedSchedule.id,
      projectId: selectedSchedule.projectId,
    }, 
    (pdfBase64) => { saveBase64Pdf(pdfBase64); }); 
  };

  const getScheduleContentJSX = () => {
    if(scheduleId === null) {
      // Blank schedules page
      return (
        <div className={`grow flex flex-col justify-center items-center gap-y-1 ${isDarkMode ? bgStyleDarkMode : bgStyle} p-1`}>
          {/* Add new schedule for project without any schedules */}
          {userCanAddSchedule()
            ?
              <button
                className='text-[#a59e9e] hover:text-yellow-500 font-semibold text-[2.5em] cursor-pointer select-none' 
                onClick={() => { setNewScheduleDialogueId(selectedProject.id, null); }}
              >
                + Create New 3-Week
              </button>
            :
              <div className='text-[#a59e9e] font-semibold text-[2.8em] select-none'>No Schedules</div>
          }
        </div>
      );
    }

    if(loadScheduleFailureMessage !== null) {
      // Failed Loading schedule message
      return (
        <div className={`grow flex flex-col justify-center gap-y-1 ${isDarkMode ? bgStyleDarkMode+' '+textStyleDarkMode : bgStyle+' '+textStyle}`}>
          <div className='flex flex-col items-center justify-center gap-4 p-4'>
            <div className='text-[1.4em] text-center'>{loadScheduleFailureMessage}</div>
            <PushButton 
              isInline={true}
              textColor='white'
              bgColor='#471cff'
              hoverBgColor='#603aff'
              onClick={() => {
                setLoadScheduleFailureMessage(null);
                setTimeout(() => {
                  const onSuccess = () => {};
                  const onFailure = (failMessage) => { setLoadScheduleFailureMessage(failMessage); };
                  siteboardAction(GET_SCHEDULE, { projectId: projectId, scheduleId: scheduleId}, onSuccess, onFailure);
                }, 500);
              }}
            >
              <div className='flex items-center gap-x-[6px]'>
                <RefreshIcon size='1.2em' color='#fff' /> 
                <div>Retry Loading Schedule</div>
              </div>
            </PushButton>
          </div>
        </div>
      );
    }

    if(scheduleId !== null && selectedSchedule === null) {
      // Loading schedule placeholder
      return (
        <div className={`grow flex flex-col justify-center gap-y-1 ${isDarkMode ? bgStyleDarkMode+' '+textStyleDarkMode : bgStyle+' '+textStyle} p-1`}>
          <div className='flex rounded-lg py-6 px-10 items-center justify-center font-semibold text-[1.5em] gap-x-4'>
            <div className='animate-[spin_1s_linear_infinite]'>
              <Logo size={48} />
            </div>
            <p className='select-none'>Loading Schedule</p>
          </div>
        </div>
      );
    }

    if(selectedSchedule === null) return <></>;

    let schedulePosition = 0;
    const projectSchedules = [];
    const scheduleActivities = [];

    const downloadPdfLocal = () => {
      setIsPreparingPdf(true);
      setTimeout(async () => {
        try {
          const pdfGenerator = await getPdfGenerator();
          const pdfBase64 = await pdfGenerator.handler({
            project: selectedProject,
            schedule: selectedSchedule,
            activities: scheduleActivities,
          });
          saveBase64Pdf(pdfBase64);
        } catch(error) {
          siteboardAction(SET_ALERT, { message: 'Error converting to PDF' }); 
        }
        setIsPreparingPdf(false);
      }, 100);
    };

    const viewPdfLocal = () => {
      setIsPreparingPdf(true);
      setTimeout(async () => {
        try {
          const pdfGenerator = await getPdfGenerator();
          const pdfBase64 = await pdfGenerator.handler({
            project: selectedProject,
            schedule: selectedSchedule,
            activities: scheduleActivities,
          });
          viewBase64Pdf(pdfBase64);
        } catch(error) {
          siteboardAction(SET_ALERT, { message: 'Error converting to PDF' }); 
        }
        setIsPreparingPdf(false);
      }, 100);
    };

    const getScheduleViewTotal = () => {
      try {
        let sharedContactScheduleStatsTotal = 0;
        let contactsViewedScheduleTotal = 0;
        let contactsConfirmedScheduleTotal = 0;
        for(const scheduleId in _contactSharedScheduleStats) {
          if(scheduleId !== selectedSchedule.id) continue;
          for(const email in _contactSharedScheduleStats[scheduleId]) {
            sharedContactScheduleStatsTotal++;
            if(_contactSharedScheduleStats[scheduleId][email].isViewed) {
              contactsViewedScheduleTotal++;
            }
            if(_contactSharedScheduleStats[scheduleId][email].isConfirmed) {
              contactsConfirmedScheduleTotal++;
            }
          }
        }
        return contactsViewedScheduleTotal;
      } catch(error) {}
      return 0;
    };

    const getPublishedByLine = () => {
      try {
        Timestamp.validate(selectedSchedule.publishedTimestamp);
        if(selectedSchedule.publishedTimestamp === 0) throw new Error();
        let publishedBy = '';
        try {
          publishedBy = _users[selectedSchedule.publishedByUserId].firstName + ' ' + _users[selectedSchedule.publishedByUserId].lastName;
        } catch(error) {}

        if(_localOnlyMode === true) {
          return <></>;
          //return (
          //  <div className='flex gap-1'>
          //    <span className='opacity-70'>Published</span>
          //    <span className='opacity-70'>on</span>
          //    <b>{format(selectedSchedule.publishedTimestamp, 'MM/dd/yyyy')}</b> 
          //  </div> 
          //);
        }

        let sharedContactScheduleStatsTotal = 0;
        let contactsViewedScheduleTotal = 0;
        let contactsConfirmedScheduleTotal = 0;
        for(const scheduleId in _contactSharedScheduleStats) {
          if(scheduleId !== selectedSchedule.id) continue;
          for(const email in _contactSharedScheduleStats[scheduleId]) {
            sharedContactScheduleStatsTotal++;
            if(_contactSharedScheduleStats[scheduleId][email].isViewed) {
              contactsViewedScheduleTotal++;
            }
            if(_contactSharedScheduleStats[scheduleId][email].isConfirmed) {
              contactsConfirmedScheduleTotal++;
            }
          }
        }

        return (
          <div className={`flex flex-col gap-1 ${isDarkMode ? '' : ''} rounded-lg max-w-[98vw] border border-neutral-700`}>
            <PushButton
              className='rounded-b border-none'
              textColor={isDarkMode ? 'white' : 'black'}
              bgColor={isDarkMode ? 'bg-neutral-600' : 'bg-neutral-300'}
              hoverBgColor={isDarkMode ? 'bg-neutral-500' : 'bg-neutral-200'}
              dropdownButton={true}
              onClick={() => { setPublishStatsShowing(!publishStatsShowing); }}
             >
              <div className='flex justify-between items-center w-full h-full'>
                <div className='flex justify-start items-center gap-x-[8px] h-full'>
                  <CircleCheckIcon size='1.2em' color='#349951' /> 
                  <div className=''>Published on {format(selectedSchedule.publishedTimestamp, 'MM/dd/yyyy')}</div>
                </div>
                <div className={publishStatsShowing ? 'scale-[-1]' : ''}>
                  <ExpandDownIcon color={isDarkMode ? '#fff' : '#000'} />
                </div>
              </div>
            </PushButton>
            {/*<div className='relative top-[-6px] h-[1px] w-full bg-[#88888888]'/>*/}
            <CollapsibleContainer isOpen={publishStatsShowing}>
              <div className='flex flex-col min-[540px]:flex-row justify-around items-center min-[540px]:items-start'>
                {sharedContactScheduleStatsTotal > 0 &&
                  <div className='flex flex-row items-end justify-start gap-2 rounded-lg max-w-screen-sm gap-0 p-4'>
                    <div className='flex flex-col items-center justify-start gap-1 w-full'>
                      <div className='flex items-center gap-2 font-bold text-sm opacity-70'>
                        {/*<EyeIcon color={isDarkMode ? 'white' : 'black'} />*/}
                        TOTAL VIEWS
                      </div>
                      <div className='font-bold text-[1.8em] tracking-[0.25em]'>{contactsViewedScheduleTotal}</div>
                      {/*<div className='text-[1.8em] tracking-[0.25em]'><b>{contactsViewedScheduleTotal}/{sharedContactScheduleStatsTotal}</b></div>*/}
                    </div>
                    <div>
                      <PushButton 
                        textColor={`black`}
                        bgColor='#f9a928'
                        hoverBgColor='#f79800'
                        onClick={() => { 
                          siteboardAction(SET_REVIEW_SHARED_SCHEDULE_VIEWS_DIALOGUE_OPEN, { isOpen: true, scheduleId: selectedSchedule.id });
                        }}
                      >
                        See Viewer Details
                      </PushButton>
                    </div>
                  </div>
                }
                <div className='flex flex-col p-4 pt-0 min-[540px]:pt-4'>
                  <div className='font-bold text-sm opacity-70 leading-[0.8em]'>PUBLISHED BY</div>
                  <div className='flex md:flex-col items-start justify-start gap-2 py-4 rounded-lg'>
                    <UserAvatar user={_users[selectedSchedule.publishedByUserId]} showOnlineStatus={false} />
                  </div>
                </div>
                {/*sharedContactScheduleStatsTotal > 0 &&
                  <div className='grow-[1]'>
                    <div className='opacity-80'>Distribution List Stats</div>
                    <div className='flex flex-col md:flex-row justify-start gap-2'>
                      <div className='flex flex-col items-start justify-between gap-2 p-4 rounded-lg grow-[1]'>
                        <div className='flex flex-row justify-between sm:flex-col gap-1 w-full'>
                          <div className='opacity-80'>Recipients Viewed Schedule</div>
                          <div className='text-[1.8em] tracking-widest'><b>{contactsViewedScheduleTotal}/{sharedContactScheduleStatsTotal}</b></div>
                        </div>
                        <PushButton 
                          textColor={`black`}
                          bgColor='#f9a928'
                          hoverBgColor='#f79800'
                          onClick={() => { 
                            siteboardAction(SET_REVIEW_SHARED_SCHEDULE_VIEWS_DIALOGUE_OPEN, { isOpen: true, scheduleId: selectedSchedule.id });
                          }}
                        >
                          See Who Viewed
                        </PushButton>
                      </div>
                      <div className='flex flex-col items-start justify-between gap-2 p-4 rounded-lg border border-[#888] grow-[1] hidden'>
                        <div className='flex flex-row justify-between sm:flex-col gap-2 w-full'>
                          <div className='opacity-80'>Recipients Confirmed Work Dates</div>
                          <div className='text-[1.8em] tracking-widest'><b>{contactsConfirmedScheduleTotal}/{sharedContactScheduleStatsTotal}</b></div>
                        </div>
                        <PushButton 
                          textColor={`black`}
                          bgColor='#f9a928'
                          hoverBgColor='#f79800'
                          onClick={() => { 
                            siteboardAction(SET_REVIEW_SHARED_SCHEDULE_CONFIRMATIONS_DIALOGUE_OPEN, { isOpen: true, scheduleId: selectedSchedule.id });
                          }}
                        >
                          See Who Confirmed
                        </PushButton>
                      </div>
                    </div>
                  </div>
                */}
              </div>
            </CollapsibleContainer>
          </div>
        );
      } catch(error) {}
      return (<></>);
    };

    const getTotalScheduleMessagesCount = () => {
      let messagesCount = 0;
      let threadsCount = 0;
      for(const scheduleMessageThreadId in _scheduleMessageThreads) {
        const messageThread = _scheduleMessageThreads[scheduleMessageThreadId];
        if(!(_userId in messageThread.members)) continue;
        if((messageThread.projectId !== selectedProject.id)) continue;
        threadsCount++;
        // TODO: don't add message to count here if all members of the thread aren't currently in the project
        for(const memberId in messageThread.members) {
          messagesCount += messageThread.members[memberId].messageCount;
        }
      }
      if(threadsCount === 0) return 'Messages';
      return messagesCount + ' Message' + (messagesCount !== 1 ? 's' : '');
    };

    const onAddActivity = () => {
      addActivity();
    };
    const onMoveRowUp = () => {
      const newScheduleActivities = [];
      let index = 0;
      while(index < scheduleActivities.length) {
        if(index+1 < scheduleActivities.length && scheduleActivities[index+1].id === selectedActivityId) {
          newScheduleActivities.push(scheduleActivities[index+1].id);
          newScheduleActivities.push(scheduleActivities[index].id);
          index += 2;
        } else {
          newScheduleActivities.push(scheduleActivities[index].id);
          index++;
        }
      }
      siteboardAction(EDIT_SCHEDULE, {
        subscriptionId: selectedProject.subscriptionId,
        schedule: {
          ...selectedSchedule,
          activitiesOrder: newScheduleActivities,
      }});
      setMovedActivityId(selectedActivityId);
    };
    const onMoveRowDown = () => {
      const newScheduleActivities = [];
      let index = 0;
      while(index < scheduleActivities.length) {
        if(index+1 < scheduleActivities.length && scheduleActivities[index].id === selectedActivityId) {
          newScheduleActivities.push(scheduleActivities[index+1].id);
          newScheduleActivities.push(scheduleActivities[index].id);
          index += 2;
        } else {
          newScheduleActivities.push(scheduleActivities[index].id);
          index++;
        }
      }
      siteboardAction(EDIT_SCHEDULE, {
        subscriptionId: selectedProject.subscriptionId,
        schedule: {
          ...selectedSchedule,
          activitiesOrder: newScheduleActivities,
      }});
      setMovedActivityId(selectedActivityId);
    };
    const onDeleteRow = () => { 
      // selected row below the one being deleted
      let selectedActivityIndex = scheduleActivities.length-1;
      for(let i = 0; i < scheduleActivities.length; i++) {
        const activity = scheduleActivities[i];
        if(activity.id === selectedActivityId) {
          selectedActivityIndex = i;
          break;
        }
      }
      if(selectedActivityIndex < scheduleActivities.length-1) {
        setSelectedActivityId(scheduleActivities[selectedActivityIndex+1].id);
      } else if(selectedActivityIndex > 0) {
        setSelectedActivityId(scheduleActivities[selectedActivityIndex-1].id);
      } else {
        setSelectedActivityId(null);
      }

      siteboardAction(DELETE_ACTIVITY, {
        activityId: _activities[selectedActivityId].id,
        projectId: _activities[selectedActivityId].projectId,
        scheduleId: _activities[selectedActivityId].scheduleId,
        subscriptionId: selectedProject.subscriptionId,
      }, () => { document.activeElement.blur(); }); 
    };

    // Build a sorted list of activities in the schedule
    // Put activities in the order the schedule specifies them first, then sort by created time
    const addedActivitiesMap = {};
    for(const activityId of (selectedSchedule.activitiesOrder || [])) {
      if(activityId in _activities && _activities[activityId].scheduleId === selectedSchedule.id) {
        scheduleActivities.push(_activities[activityId]);
        addedActivitiesMap[activityId] = '';
      }
    }
    // Add any other activities that belong to this schedule but are not specified in the sort order and sort them by creation time
    const additionalActivities = [];
    for(const key in _activities) {
      if(_activities[key].scheduleId === selectedSchedule.id && !(key in addedActivitiesMap)) {
        additionalActivities.push(_activities[key]);
      }
    }
    additionalActivities.sort((one, two) => { return one.createdTimestamp - two.createdTimestamp; });
    scheduleActivities.push(...additionalActivities);
    scheduleActivitiesRef.current = scheduleActivities;

    // Determine if move row up / down buttons are enabled, based on where selected row is in the list
    const moveActivityUpEnabled = selectedActivityId !== null && scheduleActivities.length > 1 && scheduleActivities[0].id !== selectedActivityId;
    const moveActivityDownEnabled = selectedActivityId !== null && scheduleActivities.length > 1 && scheduleActivities[scheduleActivities.length-1].id !== selectedActivityId;

    // Build a list of the schedules in the project and sort them by start date
    for(const scheduleId in (selectedProject.schedules || {}))  {
      projectSchedules.push({
        id: scheduleId,
        beginDate: selectedProject.schedules[scheduleId],
      });
    }
    projectSchedules.sort((one, two) => { // sort schedules so that the oldest are at the front of the list
      try {
        const oneDate = DateStringMMDDYYYY.toDateObject(one.beginDate);
        const twoDate = DateStringMMDDYYYY.toDateObject(two.beginDate);
        return oneDate.getTime() - twoDate.getTime();
      } catch(error) {
        return 0;
      }
    });

    // Determine the position of this schedule to display as an index
    for(let i = 0; i < projectSchedules.length; i++) {
      if(projectSchedules[i].id === selectedSchedule.id) {
        schedulePosition = i;
        break;
      }
    }

    setTimeout(() => {
      if(schedulePosition - 1 >= 0) { 
        siteboardAction(GET_SCHEDULE, { projectId: projectId, scheduleId: projectSchedules[schedulePosition - 1].id});
      }
    }, 10);
    setTimeout(() => {
      if(schedulePosition + 1 < projectSchedules.length) { 
        siteboardAction(GET_SCHEDULE, { projectId: projectId, scheduleId: projectSchedules[schedulePosition + 1].id});
      }
    }, 10);

    const dates = getDates(selectedSchedule);
    if(dates.length > 0 && datesJson === '[]') {
      setDatesJson(JSON.stringify(dates));
    }
    try {
      const datesMap = []; // map by datestring date array above
      const beginDateObject = DateStringMMDDYYYY.toDateObject(selectedSchedule.beginDate); // TODO: display an error if beginDateObject isn't valid?
      for (const date of dates) {
        datesMap[date.dateString] = '';
      }
      // Remove weather days and holiday days not in the schedule range so print view doesn't think they are there an add a row for them
      for(const key in selectedSchedule.weatherDates) {
        if(!(key in datesMap)) {
          delete selectedSchedule.weatherDates[key];
        }
      }
      for(const key in selectedSchedule.holidayDates) {
        if(!(key in datesMap)) {
          delete selectedSchedule.holidayDates[key];
        }
      }
    } catch(e) {}

    // Sum up total unread messages for this schedule's message threads
    let unreadMessagesForSchedule = 0;
    for(const unreadThread of objectValuesToList(_unreadScheduleMessageThreads)) {
      if(unreadThread.projectId !== selectedProject.id) continue;
      unreadMessagesForSchedule += unreadThread.unreadCount;
    }

    const viewPdfButtonJSX = () => {
      return (
        <PushButton 
          isLoading={isPreparingPdf}
          isInline={true}
          textColor={isDarkMode ? 'white' : 'black'}
          bgColor={isDarkMode ? 'bg-neutral-600' : 'bg-neutral-300'}
          hoverBgColor={isDarkMode ? 'bg-neutral-500' : 'bg-neutral-200'}
          onClick={viewPdfLocal}
        >
          <div className='flex items-center gap-x-[6px]'>
            <div>
            {/*<div className='drop-shadow-[0_1px_1px_rgba(80,80,80,1)]'>
              <img className='h-[1.4em]' src='/images/pdf-icon-large.png' alt='pdf icon' />*/}
              <PdfIcon size='1.6em' color={isDarkMode ? '#fff' : '#000'} />
            </div>
            <div>View PDF</div>
          </div>
        </PushButton>
      );
    };

    return (
      <div ref={mainContainer} className={`grow flex flex-col gap-y-1 ${isDarkMode ? bgStyleDarkMode+' '+textStyleDarkMode : bgStyle+' '+textStyle} py-1`}>
        {/* Schedule actions row */}
        <div className='flex items-start justify-between gap-1 px-1'>
          {_localOnlyMode === true && 
            <div className='flex items-start gap-1'>
              {viewPdfButtonJSX()}
              {/* Select start date button */}
              {userCanEditSchedule() &&
                <PushButton 
                  isInline={true}
                  textColor={isDarkMode ? 'white' : 'black'}
                  bgColor={isDarkMode ? 'bg-neutral-600' : 'bg-neutral-300'}
                  hoverBgColor={isDarkMode ? 'bg-neutral-500' : 'bg-neutral-200'}
                  onClick={() => { siteboardAction(SET_EDIT_SCHEDULE_DIALOGUE_SHOWING, { isOpen: true, scheduleId: selectedSchedule.id }); }}
                >
                  <div className='flex items-center gap-x-[6px]'>
                    <CalendarIcon size='1.4em' color={isDarkMode ? '#fff' : '#000'} />
                    <p>Select Start Date</p>
                  </div>
                </PushButton>
              }
              {_isUsingShareScheduleLink && testingRequestEmailUpdates &&
                <PushButton 
                  isInline={true}
                  textColor={`black`}
                  bgColor='#f9a928'
                  hoverBgColor='#f79800'
                  onClick={() => {
                    siteboardAction(SET_REQUEST_SCHEDULE_EMAIL_UPDATES_DIALOGUE_OPEN, { isOpen: true });
                  }}
                >
                  <div className='flex items-center gap-x-[6px]'>
                    <EmailIcon color='#000' /> 
                    <div>Request Email Updates</div>
                  </div>
                </PushButton>
              }
              {_isUsingShareScheduleLink && sharedScheduleContactEmail.length > 0 &&
                <div className={`flex flex-col justify-start items-start gap-2 rounded-lg`}>
                  {/*<div>
                    Review the scheduled work dates for _COMPANY_ below and confirm that your team can complete the work as scheduled.
                  </div>*/}
                  <PushButton 
                    textColor={`black`}
                    bgColor={sharedScheduleIsConfirmed === true ? '#00bc7d' : '#f9a928'}
                    hoverBgColor={sharedScheduleIsConfirmed === true ? '#50bc7d' : '#f79800'}
                    onClick={() => {
                      try {
                        siteboardAction(SET_CONFIRM_SHARE_SCHEDULE_WORK_DATES_DIALOGUE_OPEN, {
                          isOpen: true,
                          email: sharedScheduleContactEmail,
                          firstName: sharedScheduleContactFirstName,
                          lastName: sharedScheduleContactLastName,
                          companyName: sharedScheduleContactCompanyName,
                          shareCode: sharedScheduleCode,
                        });
                      } catch(error) {}
                    }}
                  >
                    {sharedScheduleIsConfirmed === true ?
                      <div className='flex items-center gap-x-[6px]'>
                        <CircleCheckIcon color='#000' /> 
                        <div>Work Dates Confirmed</div>
                      </div>
                    :
                      <div className='flex items-center gap-x-[6px]'>
                        <CalendarIcon size='1.4em' color='#000' />
                        <div>Confirm My Work Dates</div>
                      </div>
                    }
                  </PushButton>
                </div>
              }
            </div>
          }
          {_localOnlyMode !== true &&
          <>
          {/* Left side buttons */}
          <div className='flex items-start gap-1'>
            {/* publish schedule button */}
            {(userCanPublishSchedule() || userCanUnpublishSchedule()) &&
              <PushButton 
                //className='flex items-center bg-[#a59e9e99] hover:bg-[#efe4e4] font-semibold select-none py-[10px] px-[10px] gap-x-[5px] sm:gap-x-[12px] whitespace-nowrap'
                //className='flex items-center text-white bg-[#471cff] hover:bg-[#603aff] font-semibold select-none py-[10px] px-[10px] gap-x-[5px] sm:gap-x-[12px] rounded-[10px] whitespace-nowrap'
                isInline={true}
                textColor={isDarkMode ? 'white' : 'black'}
                bgColor={selectedSchedule.isPublished ?  (isDarkMode ? 'bg-emerald-600' : 'bg-emerald-500') : (isDarkMode ? 'bg-neutral-600' : 'bg-neutral-300')}
                hoverBgColor={selectedSchedule.isPublished ? (isDarkMode ? 'bg-emerald-500' : 'bg-emerald-400') : (isDarkMode ? 'bg-neutral-500' : 'bg-neutral-200')}
                title='Email schedule to project members & contacts'
                onClick={() => {
                  setPublishScheduleDialogueId(selectedSchedule.id);
                }}
              >
                {!selectedSchedule.isPublished
                  ?
                    <div className='flex items-center gap-1.5'>
                      <Send size={16} color={isDarkMode ? '#fff' : '#000'} />
                      <p className=''>Publish & Send</p>
                    </div>
                  :
                    <div className='flex items-center gap-1.5'>
                      <CircleCheck size='1.2em' color={isDarkMode ? '#fff' : '#000'} />
                      <p className=''>Published</p>
                      <div className={`h-full w-[1px] ${isDarkMode ? 'bg-white' : 'bg-black'} mx-1`}></div>
                      <p className=''>{getScheduleViewTotal()} Views</p>
                    </div>
                }
              </PushButton>
            }
            {/* Add new schedule button / add 3 week button */}
            {userCanAddSchedule() &&
              <PushButton 
                isInline={true}
                title='Create a schedule for next week'
                textColor={isDarkMode ? 'white' : 'black'}
                bgColor={isDarkMode ? 'bg-neutral-600' : 'bg-neutral-300'}
                hoverBgColor={isDarkMode ? 'bg-neutral-500' : 'bg-neutral-200'}
                onClick={() => { setNewScheduleDialogueId(selectedProject.id, selectedSchedule.id); }}
              >
                <div className='flex items-center gap-x-[6px]'>
                  <AddIcon color={isDarkMode ? '#fff' : '#000'} />
                  <p>New 3-Week</p>
                </div>
              </PushButton>
            }
            <PushButton 
              isInline={true}
              textColor={isDarkMode ? 'white' : 'black'}
              bgColor={isDarkMode ? 'bg-neutral-600' : 'bg-neutral-300'}
              hoverBgColor={isDarkMode ? 'bg-neutral-500' : 'bg-neutral-200'}
              onClick={() => {
                siteboardAction(SET_SHARE_LINK_DIALOGUE_SHOWING, { isOpen: true, scheduleId: selectedSchedule.id });
              }}
            >
              <div className='flex items-center gap-x-[6px]'>
                <LinkIcon size='1.4em' color={isDarkMode ? '#fff' : '#000'} />
                <div>Share Link</div>
              </div>
            </PushButton>
            {/* unpublish schedule button */}
            {/*{userCanUnpublishSchedule() &&
              <PushButton 
                isInline={true}
                textColor={isDarkMode ? 'white' : 'black'}
                bgColor={isDarkMode ? 'bg-neutral-600' : 'bg-neutral-300'}
                hoverBgColor={isDarkMode ? 'bg-neutral-500' : 'bg-neutral-200'}
                onClick={() => { 
                  siteboardAction(UNPUBLISH_SCHEDULE, {
                    scheduleId: selectedSchedule.id,
                    projectId: selectedProject.id,
                    subscriptionId: selectedProject.subscriptionId,
                  }, (output) => { });
                  //setUnpublishScheduleDialogueId(selectedSchedule.id); 
                }}
              >
                <div className='flex items-center gap-x-[6px]'>
                  <UnlockIcon size={16} color={isDarkMode ? '#fff' : '#000'} />
                  <p>Unlock 3-Week</p>
                </div>
              </PushButton>
            }*/}
          </div>
          {/* Right side buttons */}
          <div className='flex items-start gap-1'>
            {/* Messages button */}
            <div className='relative'>
              <PushButton 
                isInline={true}
                textColor={isDarkMode ? 'white' : 'black'}
                bgColor={isDarkMode ? 'bg-neutral-600' : 'bg-neutral-300'}
                hoverBgColor={isDarkMode ? 'bg-neutral-500' : 'bg-neutral-200'}
                onClick={() => { 
                  siteboardAction(SET_SCHEDULE_MESSAGES_DIALOGUE_ID, { projectId: selectedProject.id, scheduleMessageThreadId: '00000000-0000-0000-0000-000000000000' });
                }}
              >
                <div className='flex items-center gap-x-[6px]'>
                  <CommentIcon color={isDarkMode ? '#fff' : '#000'} size='1.2em' />
                  <p>{getTotalScheduleMessagesCount()}</p>
                </div>
              </PushButton>
              {unreadMessagesForSchedule > 0 &&
                <div className='absolute top-[-8px] right-[-6px]'>
                  <AlertCountBubble totalUnreadMessages={unreadMessagesForSchedule} />
                </div>
              }
            </div>
            {viewPdfButtonJSX()}
            {/* delete schedule button */}
            {userCanDeleteSchedule() ?
              <div className='relative group self-center'>
                <button
                  className={`flex items-center border border-transparent hover:border-neutral-800 ${isDarkMode ? 'text-white bg-neutral-600 group-hover:bg-neutral-500' : 'text-black bg-neutral-300 group-hover:bg-neutral-200'} font-semibold select-none py-[9px] px-[10px] gap-x-2 whitespace-nowrap rounded-lg`}
                  onClick={() => { }}
                >
                  <div><Ellipsis /></div>
                </button>
                <div className={`absolute z-[1] hidden group-hover:flex top-full translate-y-[9px] right-0 flex-col py-2 ${isDarkMode ? 'bg-neutral-700' : 'bg-neutral-300'} rounded-lg shadow animate-up-fade`}>
                  <PushButton 
                    textColor={isDarkMode ? 'white' : 'black'}
                    bgColor={isDarkMode ? 'bg-neutral-600' : 'bg-neutral-300'}
                    hoverBgColor={isDarkMode ? 'bg-neutral-500' : 'bg-neutral-200'}
                    onClick={() => { setDeleteScheduleDialogueId(selectedSchedule.id); }}
                  >
                    <div className='flex items-center gap-x-[6px]'>
                      <TrashIcon color={isDarkMode ? '#fff' : '#000'} />
                      <div>Delete 3-Week</div>
                    </div>
                  </PushButton>
                </div>
              </div>
              :
              <div></div>
            }
            {/*userCanDeleteSchedule() &&
              <PushButton 
                isInline={true}
                textColor={isDarkMode ? 'white' : 'black'}
                bgColor={isDarkMode ? 'bg-neutral-600' : 'bg-neutral-300'}
                hoverBgColor={isDarkMode ? 'bg-neutral-500' : 'bg-neutral-200'}
                onClick={() => { setDeleteScheduleDialogueId(selectedSchedule.id); }}
              >
                <div className='flex items-center gap-x-[6px]'>
                  <TrashIcon color={isDarkMode ? '#fff' : '#000'} />
                  <div>Delete 3-Week</div>
                </div>
              </PushButton>
            */}
          </div>
          </>
          }
        </div>
        {/* Schedule row */}
        <div className='grow flex flex-col'>
          <div className='flex flex-col overflow-y-scroll px-1'>
            <div className='flex-row-reverse justify-between items-start'>
              {/* Published by row */}
              {/*selectedSchedule.isPublished && 
                <div className='grow-[1] mb-1'>{getPublishedByLine()}</div>
              */}
              {/* date range row */}
              <div className='grow-[1] flex items-center'>
                <div className={``}>
                  <div className=''>
                    <div className='inline-block opacity-70'>Date Range:&nbsp;</div>
                    <div className='inline-block font-bold'>{dates.length > 0 ? dates[0].dateString : ''}</div>
                    <div className='inline-block px-0.5'>-</div>
                    <div className='inline-block font-bold'>{dates.length > 0 ? dates[dates.length-1].dateString : ''}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className='w-full h-1.5'></div>
            {/* Holiday days row. don't need to show this */}
            {(userCanEditSchedule() || keyCountInObject(selectedSchedule.holidayDates) > 0) &&
              <div className='flex w-full'>
                <div className={`flex justify-end ${leftTableWidth}`}>
                  <div className='flex gap-x-2 items-center mr-[8px]'>
                    <HolidayIcon active={true} size={18} color='#fff' fill='#000' />
                    <p className='lg:hidden select-none opacity-70'>Holiday</p>
                    <p className='hidden lg:block select-none opacity-70'>Holiday - No Work</p>
                  </div>
                </div>
                <div className={`flex ${rowHeight} ${rightTableWidth}`}>
                  {dates.map((date, index) => (
                    <div  
                      key={index} 
                      className={`group flex items-center justify-center ${cellWidth} border ${isDarkMode ? cellBorderColorRightDarkMode : cellBorderColor} border-dashed border-r-0 border-b-0 last:border-r ${userCanEditSchedule() === true ? 'cursor-pointer ' : ''}`}
                      onClick={() => {
                        if(userCanEditSchedule() !== true && userCanUnpublishSchedule()) {
                          siteboardAction(SET_ALERT, { message: scheduleLockedWarning }); 
                          return;
                        }
                        if(userCanEditSchedule() !== true) return;

                        const newHolidayDates = {...(selectedSchedule.holidayDates || {})};
                        if(date.dateString in newHolidayDates) {
                          delete newHolidayDates[date.dateString];
                        } else {
                          newHolidayDates[date.dateString] = '';
                        }
                        siteboardAction(EDIT_SCHEDULE, {
                          subscriptionId: selectedProject.subscriptionId,
                          schedule: {
                            ...selectedSchedule,
                            holidayDates: newHolidayDates,
                        }});
                      }}
                    >
                      <div className='flex overflow-hidden h-full grow w-0 justify-center items-center'>
                        {date.dateString in selectedSchedule.holidayDates
                          ? 
                            <div>
                              <HolidayIcon active={true} size={20} color={fgColorDarkMode} fill={isDarkMode ? bgStyleDarkMode : bgStyle} />
                            </div>
                          :
                            <div className={`hidden ${(!_isTouchDevice && userCanEditSchedule()) ? 'group-hover:block' : ''}`}>
                              <HolidayIcon active={false} size={20} color={isDarkMode ? '#fff' : '#000'} fill={"#000"} />
                            </div>
                        }
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            }
            {/* weather days row */}
            {(userCanEditSchedule() || keyCountInObject(selectedSchedule.weatherDates) > 0) &&
              <div className='flex w-full'>
                <div className={`${leftTableWidth} flex justify-end`}>
                  <div className='flex gap-x-2 items-center mr-[8px]'>
                    <RainIcon color={isDarkMode ? '#fff' : '#000'} />
                    <p className='sm:hidden select-none opacity-70'>Weather</p>
                    <p className='hidden sm:block select-none opacity-70'>Weather days</p>
                  </div>
                </div>
                <div className={`flex ${rowHeight} ${rightTableWidth}`}>
                  {dates.map((date, index) => (
                    <div
                      key={index} 
                      className={`group flex items-center justify-center ${cellWidth} border ${isDarkMode ? cellBorderColorRightDarkMode : cellBorderColor} border-dashed border-r-0 border-b-0 last:border-r ${userCanEditSchedule() === true ? 'cursor-pointer ' : ''}`}
                      onClick={() => {
                        if(userCanEditSchedule() !== true && userCanUnpublishSchedule()) {
                          siteboardAction(SET_ALERT, { message: scheduleLockedWarning }); 
                          return;
                        }
                        if(userCanEditSchedule() !== true) return;

                        const newWeatherDates = {...(selectedSchedule.weatherDates || {})};
                        if(date.dateString in newWeatherDates) {
                          delete newWeatherDates[date.dateString];
                        } else {
                          newWeatherDates[date.dateString] = '';
                        }
                        siteboardAction(EDIT_SCHEDULE, {
                          subscriptionId: selectedProject.subscriptionId,
                          schedule: {
                            ...selectedSchedule,
                            weatherDates: newWeatherDates,
                        }});
                      }}
                    >
                      <div className='flex overflow-hidden h-full grow w-0 justify-center items-center'>
                        {date.dateString in selectedSchedule.weatherDates 
                          ? 
                            <div className={`flex justify-center items-center w-full h-full bg-[#1267e7]`}>
                              <RainIcon color='#fff' />
                            </div>
                          :
                            <div className={`hidden w-full h-full ${(!_isTouchDevice && userCanEditSchedule()) ? 'group-hover:flex justify-center items-center' : ''}`}>
                              <RainIcon color={isDarkMode ? '#fff' : '#000'} />
                            </div>
                        }
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            }
            {/* month columns (ie Jan, Feb) row */}
            {monthCellsJSX}
            {/* row buttons and day of week (ie Thu, Fri) row */}
            <div className='flex w-full'>
              <div className={`relative ${leftTableWidth}`}>
                {userCanEditSchedule() &&
                  <div className={`absolute top-[-23px] w-full flex justify-between`}>
                    {/*<button 
                      className='px-2 bg-[#a59e9e99] hover:bg-[#efe4e4] text-[#a59e9e] hover:text-yellow-500 font-semibold text-[2.8em] cursor-pointer select-none'
                      title='Auto sort rows'
                    >
                      <SortIcon size='16' color='black' />
                    </button>*/}

                    {/* Add activity button  */}
                    <PushButton 
                      id={onNewRowId}
                      textColor={isDarkMode ? 'white' : 'black'}
                      bgColor={isDarkMode ? 'bg-neutral-600' : 'bg-neutral-300'}
                      hoverBgColor={isDarkMode ? 'bg-neutral-500' : 'bg-neutral-200'}
                      isInline={true}
                      title='Add new activity row'
                      onClick={onAddActivity}
                    >
                      <div className='flex items-center gap-x-[6px]'>
                        <AddIcon color={isDarkMode ? '#fff' : '#000'} />
                        <p className=''>Add Activity</p>
                      </div>
                    </PushButton>

                    <div className='flex justify-center gap-x-0'>
                      {/* Move row up button */}
                      <PushButton 
                        id={onMoveRowUpId}
                        isInline={true}
                        textColor={isDarkMode ? 'white' : 'black'}
                        bgColor={isDarkMode ? 'bg-neutral-600' : 'bg-neutral-300'}
                        hoverBgColor={isDarkMode ? 'bg-neutral-500' : 'bg-neutral-200'}
                        disabled={!moveActivityUpEnabled}
                        title='Move up selected row'
                        onClick={onMoveRowUp}
                      >
                        <div className='flex items-center scale-y-[-1] px-1'>
                          <ExpandDownIcon color={isDarkMode ? '#fff' : '#000'} />
                        </div>
                      </PushButton>
                      {/* Move row down button */}
                      <PushButton 
                        id={onMoveRowDownId}
                        isInline={true}
                        textColor={isDarkMode ? 'white' : 'black'}
                        bgColor={isDarkMode ? 'bg-neutral-600' : 'bg-neutral-300'}
                        hoverBgColor={isDarkMode ? 'bg-neutral-500' : 'bg-neutral-200'}
                        disabled={!moveActivityDownEnabled}
                        title='Move down selected row'
                        onClick={onMoveRowDown}
                      >
                        <div className='flex items-center px-1'>
                          <ExpandDownIcon color={isDarkMode ? '#fff' : '#000'} />
                        </div>
                      </PushButton>
                    </div>
                    <div className='flex justify-center'>
                      {/* Delete row down button */}
                      <PushButton 
                        id={onDeleteRowId}
                        isInline={true}
                        textColor={'white'}
                        bgColor={'bg-red-800'}
                        hoverBgColor={'bg-red-600'}
                        title='Delete selected row'
                        disabled={selectedActivityId === null}
                        onClick={onDeleteRow}
                      >
                        <div className='flex items-center gap-x-[6px]'>
                          <TrashIcon color={'white'} />
                          <p className='hidden min-[890px]:block'>Delete Row</p>
                        </div>
                      </PushButton>
                      {/* placeholder for the toggle critical button column, so the first two columns line up with the ones below */}
                      <div className={'w-0 lg:w-auto flex h-full px-[4px] pointer-events-none opacity-0'}>
                        <button className='sm:px-[2px]' >
                          <CriticalIcon color='#fff' />
                        </button>
                      </div>
                    </div>
                  </div>
                }
              </div>
              {/* day of week (ie Thu, Fri) row */}
              {daysOfWeekJSX}
            </div>
            {/* ActivityLines header and day of month (ie 01, 02) row */}
            <div className='flex w-full'>
              <div className={`flex items-center ${leftTableWidth} ${rowHeight}`}>
                <div className={'flex items-center h-full font-bold text-[1.1em] '+(isDarkMode ? cellBorderColorDarkMode : cellBorderLeftColor)+' '+responsibleColWidth}>
                   <div className={`w-full h-full ${isDarkMode ? bgStyleDarkMode : bgStyle}`}>Responsible</div>
                </div>
                <div className={'flex items-center h-full font-bold text-[1.1em] '+(isDarkMode ? cellBorderColorDarkMode : cellBorderLeftColor)+' '+descriptionColWidth}>
                   <div className={`w-full h-full ${isDarkMode ? bgStyleDarkMode : bgStyle}`}>Description</div>
                </div>
                {/* placeholder for the toggle critical button column, so the first two columns line up with the ones below */}
                {userCanEditSchedule() && 
                  <div className={'flex h-full px-[4px] opacity-0'}>
                    <button className='sm:px-[2px]' >
                      <CriticalIcon />
                    </button>
                  </div>
                }
              </div>
              {dayOfMonthJSX}
            </div>
          </div>
          {/* ActivityLine rows */}
          <div className='relative grow flex flex-col'>
            <div 
              ref={activityContainer} 
              className='relative top-[-2px] grow flex flex-col justify-start items-start overflow-y-scroll basis-0 h-0 px-1 pb-1 pt-[2px]'
              onScroll={updateScrollTopBottomIndicators}
            >
              <div className={`w-full`}>
                {scheduleActivities.map((activity, index) => (
                  <ActivityLineMulti
                    key={activity.id}
                    activity={activity}
                    subscriptionId={selectedProject.subscriptionId}
                    dates={JSON.stringify(dates)} 
                    holidayDates={JSON.stringify(selectedSchedule.holidayDates)}
                    editingEnabled={userCanEditSchedule()}
                    siteboardAction={siteboardAction}
                    isSelected={selectedActivityId === activity.id}
                    setSelectedActivityId={setSelectedActivityId} 
                    isNew={newActivityId === activity.id}
                    setNewActivityId={setNewActivityId}
                    isMoved={movedActivityId === activity.id}
                    setMovedActivityId={setMovedActivityId}
                    isDarkMode={isDarkMode}
                    activityContainer={activityContainer}
                    userCanUnpublishSchedule={userCanUnpublishSchedule()}
                    descriptionInputLastFocused={descriptionInputLastFocused}
                  />
                ))}
              </div>
            </div>

            {/* More Top content gradient */}
            <div
              className={`
                pointer-events-none absolute inset-x-0 top-[-2px] h-6
                origin-top
                ${isDarkMode
                  ? 'bg-gradient-to-b from-zinc-950 to-transparent'
                  : 'bg-gradient-to-b from-zinc-900/50 to-transparent'
                }
                transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]
                ${activityListAtTop ? 'scale-y-0' : 'scale-y-100'}
              `}
            />

            {/* More Bottom content gradient */}
            <div
              className={`
                pointer-events-none absolute inset-x-0 bottom-0 h-6
                origin-bottom
                ${isDarkMode
                  ? 'bg-gradient-to-t from-zinc-950 to-transparent'
                  : 'bg-gradient-to-t from-zinc-900/50 to-transparent'
                }
                transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]
                ${activityListAtButtom ? 'scale-y-0' : 'scale-y-100'}
              `}
            />

          </div>
        </div>

        {/* Schedule Version row */}
        {_localOnlyMode !== true &&
          <div className='flex gap-2 p-1'>

            {/* Next/prev schedule navigation */}
            <div className='flex items-center scale-[0.9] sm:scale-[1]'>
              <button
                className={`${schedulePosition - 1 >= 0 ? '' : 'cursor-not-allowed opacity-20'}`}
                title={schedulePosition - 1 >= 0 ? 'Go to previous schedule' : 'Already at oldest schedule'}
                onMouseEnter={() => { 
                  if(schedulePosition - 1 >= 0) { 
                    siteboardAction(GET_SCHEDULE, { projectId: projectId, scheduleId: projectSchedules[schedulePosition - 1].id});
                  }
                }}
                onClick={() => { 
                  if(schedulePosition - 1 >= 0) { 
                    setPath('projectId#' + selectedProject.id + '#scheduleId#' + projectSchedules[schedulePosition - 1].id); 
                  }
                }}
              >
                <BackIcon size={36} color={isDarkMode ? fgColorDarkMode : fgColor} />
              </button>
              <div className='flex flex-col items-center justify-center'>
                <div className=''>{schedulePosition+1} / {projectSchedules.length}</div>
                <div className='text-xs opacity-50 uppercase font-bold'>Version</div>
              </div>
              <button 
                className={`scale-x-[-1] ${schedulePosition + 1 < projectSchedules.length ? '' : 'cursor-not-allowed opacity-20'}`}
                title={schedulePosition + 1 < projectSchedules.length ? 'Go to next schedule' : 'Already at most recent schedule'}
                onMouseEnter={() => { 
                  if(schedulePosition + 1 < projectSchedules.length) { 
                    siteboardAction(GET_SCHEDULE, { projectId: projectId, scheduleId: projectSchedules[schedulePosition + 1].id});
                  }
                 }}
                onClick={() => { 
                  if(schedulePosition + 1 < projectSchedules.length) { 
                    setPath('projectId#' + selectedProject.id + '#scheduleId#' + projectSchedules[schedulePosition + 1].id); 
                  } 
                }}
              >
                <BackIcon size={36} color={isDarkMode ? fgColorDarkMode : fgColor} />
              </button>
            </div>

            {/* Old Version / Newest Version Label */}
            <div className='grow flex justify-start items-center'>
              <div
                className={`
                  inline-flex items-center justify-center
                  border-4 ${schedulePosition < (projectSchedules.length-1) ? 'border-red-700 text-red-700' : 'border-emerald-600 text-emerald-600'} font-black
                  uppercase tracking-[0.25em]
                  px-6 py-1.5 rounded-md
                  opacity-80
                  select-none
                  shadow-[0_0_2px_rgba(127,29,29,0.5)]
                  text-sm
                `}
              >
                {schedulePosition < (projectSchedules.length-1) ? 'Old Version' : 'Newest Version'}
              </div>
            </div>
            {/*userCanViewGeneralNotes() &&
              <div className='grow flex flex-col ml-8'>
                <textarea 
                  className={`${isDarkMode ? bgStyleDarkMode + ' shadow-neutral-900 shadow-md hover:border-gray-500' : bgStyle + ' shadow-neutral-300 shadow-sm hover:border-gray-500 focus:bg-yellow-50'} leading-[1.1em] border ${isDarkMode ? cellBorderColorDarkMode : cellBorderColor} p-2.5 resize-none rounded-lg focus:border-transparent `}
                  value={typeof selectedSchedule.generalNotes === 'string' ? selectedSchedule.generalNotes : ''}
                  disabled={userCanEditSchedule() !== true}
                  rows={3}
                  placeholder='General Notes'
                  onChange={(event) => {
                    siteboardAction(EDIT_SCHEDULE, {
                      subscriptionId: selectedProject.subscriptionId,
                      schedule: {
                        ...selectedSchedule,
                        generalNotes: event.target.value,
                    }});
                  }}
                >
                </textarea>
              </div>
            */}
          </div>
        }
        {/* Old Schedule Version Warning Overlay */}
        {schedulePosition < (projectSchedules.length-1) && 
          <div ref={oldScheduleOverlayRef} className='fixed flex justify-center items-center pointer-events-none hidden'>
            <div className={`font-bold text-[12vw] lg:text-[7em] opacity-40 leading-[1em] translate-y-[100%] text-red-500`}>OLD VERSION</div>
          </div>
        }
      </div>
    );
  };

  if(selectedProject === null) {
    // Return error page if the project for this schedule doesn't exist
    return (
      <div className={`w-full h-full flex flex-col justify-center gap-y-1 text-white`}>
        <div className='flex flex-col items-center justify-center gap-4 p-4'>
          <div className='text-[1.4em] text-center'>Error: Project for this schedule does not exist</div>
          <button  
            className={`flex items-center text-white bg-neutral-600 hover:bg-neutral-500 font-semibold select-none py-[10px] px-[10px] gap-x-[5px] sm:gap-x-[12px] whitespace-nowrap rounded-lg`}
            onClick={() => { setPath(''); }}
          >
            Go Back to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='w-full h-full flex flex-col overflow-y-hidden bg-black/90'>
      {_localOnlyMode === true &&
        <div className='flex items-center justify-between p-2 pb-0'>
          <a href="/" target="_blank" rel="noopener noreferrer">
            <div className={'flex items-center justify-center gap-x-1'}>
              <Logo size={'1.4em'} />
              <span className='font-bold text-[1.4em] text-white'>{process.env.REACT_APP_WEBSITE_NAME}</span>
            </div>
          </a>
          <div className='grow hidden min-[500px]:flex justify-center items-center gap-x-[8px]'>
            <div className='sm:hidden'><GantChartIcon size='24' color='#8060ff' /></div>
            <div className='hidden sm:block'><GantChartIcon size='1.8em' color='#8060ff' /></div>
            <p className='text-[1.4em] font-normal text-[#fff] whitespace-nowrap'>3-Week Schedule</p>
          </div>
          <div className='md:hidden'>
            <DarkModeToggle small={true}/>
          </div>
          <div className='hidden md:block'>
            <DarkModeToggle />
          </div>
        </div>
      }
      {/* Title bar */}
      <div className={'flex justify-between items-center gap-x-8 p-2 whitespace-nowrap lg:whitespace-normal'}>
        {/* Project name */}
        <div className={`flex flex-col ${_localOnlyMode ? 'w-full' : ''}`}>
          <div className='w-full'>
            {(_localOnlyMode === true && userCanEditSchedule()) ?
              <input
                className='w-full text-[2em] text-[#f9a928] font-bold leading-[1em] bg-black/90 border border-1 border-black hover:border-white px-1 tracking-tight' 
                type="text"
                value={selectedProject.name}
                onChange={(event) => {
                  siteboardAction(EDIT_PROJECT, {
                    project: {
                      ...selectedProject,
                      name: event.target.value,
                    }
                  });
                  if(_localOnlyMode) {
                    document.title = `${event.target.value.trim()} 3-Week`;
                  }
                }}
              />
            :
              <div className='inline text-[1.6em] sm:text-[2em] text-[#f9a928] font-bold leading-[1em] tracking-tight'>{selectedProject.name}</div>
            }
            {(typeof selectedProject.jobNumber === 'string' && selectedProject.jobNumber.length > 0) &&
              <div className='inline text-[1em] text-white/70 leading-[1em] whitespace-nowrap'> <Hash size={16} className='inline relative top-[-2px]' />{selectedProject.jobNumber}</div>
            }
          </div>
          {(typeof selectedProject.address === 'string' && selectedProject.address.length > 0) &&
            <div className='hidden lg:block text-white/70'>
              <div className='inline-block align-middle'><MapPin size={16} /></div> <p className='inline-block text-[1em] whitespace-nowrap'>{(selectedProject.address || '').replaceAll(/\n/g, ', ').replaceAll(/,+/g, ',')}</p>
            </div>
          }
        </div>
        {/* 3-Week Title */}
        {_localOnlyMode !== true &&
          <div className='grow hidden xl:flex justify-center items-center gap-x-[8px]'>
            <div className='sm:hidden'><GantChartIcon size='24' color='#8060ff' /></div>
            <div className='hidden sm:block'><GantChartIcon size='1.8em' color='#8060ff' /></div>
            <p className='text-[1.4em] font-normal text-[#fff] whitespace-nowrap'>3-Week Schedule</p>
          </div>
        }
        {/* PDF and dark mode buttons  */}
        {_localOnlyMode !== true &&
          <div className='hidden xl:flex gap-x-6 items-center'>
            <DarkModeToggle />
            {/*scheduleId !== null &&
              <PushButton 
                textColor={isDarkMode ? 'white' : 'black'}
                bgColor={isDarkMode ? 'bg-neutral-600' : 'bg-neutral-300'}
                hoverBgColor={isDarkMode ? 'bg-neutral-500' : 'bg-neutral-200'}
                onClick={downloadPdf}
              >
                <div className='flex items-center gap-x-[6px]'>
                  <div className='drop-shadow-[0_1px_1px_rgba(80,80,80,1)]'>
                    <img className='h-[24px]' src='/images/pdf-icon-large.png' alt='pdf icon' />
                  </div>
                  <div>Save PDF</div>
                </div>
              </PushButton>
            */}
          </div>
        }
      </div>
      {/* Main Content */}
      <div className={'grow flex min-h-0'}>
        <div className='grow flex flex-col'>
          {getScheduleContentJSX()}
          {/*_localOnlyMode !== true &&
            <div className='hidden lg:block'>
              <PoweredByBar />
            </div>
          */}
        </div>
      </div>
    </div>
  );
};

const ActivityLineSingle = ({
  activity,
  subscriptionId,
  siteboardAction,
  setSelectedActivityId,
  isNew = false,
  setNewActivityId,
  isMoved = false,
  setMovedActivityId,
  activityContainer,
  userCanUnpublishSchedule = false,
  dates = '[]', // a JSON array, stringified to minimize re-rendering
  holidayDates = '{}', // a JSON map, stringified to minimize re-rendering
  editingEnabled = false,
  isSelected = false,
  isDarkMode = false,
  descriptionInputLastFocused,
}) => {

  const [isMousedOver, setIsMousedOver] = useState(false);

  const containerRef = useRef(null);
  const inputResponsibleRef = useRef(null);
  const inputDescriptionRef = useRef(null);

  // bring activity into view if it is selected, if it has been moved up/down it can move out of view
  // this gets triggered when the activity line rows are reordered after moving a row up/down
  useEffect(() => {
    // focus input when newly created activity is shown
    if(isNew === true) {
      if(descriptionInputLastFocused.current === true) {
        inputDescriptionRef.current.focus();
      } else {
        inputResponsibleRef.current.focus();
      }
      try { setNewActivityId(null); } catch(error) {} // set the activity as no longer new
      return;
    }

    if (isSelected !== true) return;

    // scroll activity line into view if it is not visible
    try {
      const activityContainerBounds = activityContainer.current.getBoundingClientRect();
      const activityLineBounds = containerRef.current.getBoundingClientRect();
      if(activityLineBounds.bottom > (activityContainerBounds.bottom-10) || activityLineBounds.top < (activityContainerBounds.top+10)) {
        // activity line is not fully visible
        containerRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    } catch(error) {}

    if(isMoved) {
      try { setMovedActivityId(null); } catch(error) {} // set the activity as no longer newly moved
    }
  }, [isSelected, isNew, isMoved, setNewActivityId, setMovedActivityId, activityContainer, descriptionInputLastFocused]);

  // blur inputs when escape is pressed
  useEffect(() => {
    const onKeyUp = (event) => {
      if(event.key === 'Escape') { 
        inputResponsibleRef.current.blur();
        inputDescriptionRef.current.blur();
        if(isSelected) {
          setSelectedActivityId(null);
        }
      }
    };
    const onKeyDown = (event) => {
    };

    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('keydown', onKeyDown);
    return () => { // remove listener when component unloads
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('keydown', onKeyDown); 
    };
  }, [setSelectedActivityId, isSelected]);

  // Measure a hidden clone because the textarea's scrollHeight cannot report content shorter than its current height.
  function getTextareaContentHeight(element) {
    if (!element) return 0;

    const styles = window.getComputedStyle(element);
    const clone = element.cloneNode(false);

    clone.removeAttribute("id");
    clone.removeAttribute("name");
    clone.setAttribute("aria-hidden", "true");
    clone.tabIndex = -1;

    // Preserve font, padding, wrapping, and other layout styles.
    for (const property of styles) {
      clone.style.setProperty(property, styles.getPropertyValue(property));
    }

    clone.value = element.value;
    clone.rows = 1;

    Object.assign(clone.style, {
      position: "fixed",
      top: "0",
      left: "-10000px",
      visibility: "hidden",
      pointerEvents: "none",
      boxSizing: "border-box",
      width: styles.width,
      height: "0px",
      minHeight: "0px",
      maxHeight: "none",
      overflow: "hidden",
      transition: "none",
      animation: "none",
    });

    document.body.appendChild(clone);
    const height = clone.scrollHeight;
    clone.remove();

    return height;
  }

  const updateInputHeights = useCallback(() => {
    const inputResponsible = inputResponsibleRef.current;
    const inputDescription = inputDescriptionRef.current;
    if (!inputResponsible || !inputDescription) return;

    // need to set to auto so the scrollHeight will be updated if the textarea content shrinks
    //inputResponsible.style.height = 'auto';
    //inputDescription.style.height = 'auto';

    //const inputHeight = Math.max(inputResponsible.scrollHeight, inputDescription.scrollHeight);
    const inputHeight = Math.max(getTextareaContentHeight(inputResponsible), getTextareaContentHeight(inputDescription));
    inputResponsible.style.height = `${inputHeight}px`;
    inputDescription.style.height = `${inputHeight}px`;

    // scroll activity line into view if it is not visible
    try {
      if(!isSelected) throw new Error();
      const activityContainerBounds = activityContainer.current.getBoundingClientRect();
      const activityLineBounds = containerRef.current.getBoundingClientRect();
      if(activityLineBounds.bottom > (activityContainerBounds.bottom-10) || activityLineBounds.top < (activityContainerBounds.top+10)) {
        // activity line is not fully visible
        containerRef.current.scrollIntoView({
          behavior: 'instant',
          block: 'center',
        });
      }
    } catch(error) {}
  }, [isSelected, activityContainer]);

  //useEffect(() => {
  //  window.addEventListener("resize", updateInputHeights);
  //  return () => {
  //    window.removeEventListener("resize", updateInputHeights);
  //  };
  //}, [updateInputHeights]);

  //useLayoutEffect(() => {
  //  updateInputHeights();
  //}, [activity.responsible, activity.description, updateInputHeights]);

  const activityJSX = useMemo(() => {
    const datesList = JSON.parse(dates);
    const holidayDatesMap = JSON.parse(holidayDates);

    return (
    <div ref={containerRef} className='group relative overflow-visible'>
      <div  
        className={'flex w-full break-inside-avoid activityLine ' + (isSelected ? '' : '')}
        onMouseEnter={() => { setIsMousedOver(true); }}
        onMouseLeave={() => { setIsMousedOver(false); }}
      >
        {/* Highlight mouse-over overlay */}
        {(isMousedOver && !isSelected) && 
          <div className={`absolute z-[2] left-0 top-0 w-full h-full pointer-events-none ${isDarkMode ? 'bg-neutral-400 opacity-20' : 'bg-neutral-400 opacity-20'}`}></div>
        }
        {/* Left columns */}
        <div className={`flex items-start ${leftTableWidth} ${rowHeight}`}>
          {/* Responsible */}
          <div className={`flex items-start border ${isDarkMode ? cellBorderColorDarkMode : cellBorderLeftColor} border-r-0 border-t-0 group-first:border-t ${responsibleColWidth} ${rowHeight}`}>
            <input
              ref={inputResponsibleRef}
              className={`
                w-full h-full 
                px-1 sm:px-2 
                focus:z-[1]
                ${isDarkMode 
                  ? `${textStyleDarkMode} ${isSelected ? bgStyleDarkModeSelected : bgStyleDarkMode}`
                  : `${textStyle} ${isSelected ? 'bg-yellow-50' : bgStyle}`
                }
              `}
              type='text' 
              placeholder='responsible'
              disabled={editingEnabled === false}
              value={activity.responsible}
              onFocus={() => {
                try { setSelectedActivityId(activity.id); } catch(error) {} 
                descriptionInputLastFocused.current = false;
              }}
              onChange={(event) => {
                siteboardAction(EDIT_ACTIVITY, {
                  subscriptionId: subscriptionId,
                  activity: {
                    ...activity,
                    responsible: event.target.value,
                  }
                });
              }}
              onKeyDown={(event) => {
                if(event.key === 'ArrowUp') {
                  event.preventDefault();
                } else if(event.key === 'ArrowDown') {
                  event.preventDefault();
                } else if(event.key === 'Enter') {
                  event.preventDefault();
                }
              }}
            />
          </div>
          {/* Description */}
          <div className={`flex items-start border ${isDarkMode ? cellBorderColorDarkMode : cellBorderLeftColor} border-t-0 group-first:border-t ${descriptionColWidth} border-r-0 ${rowHeight} ${(editingEnabled) && 'border-r-[1px]'}`}>
            <input 
              ref={inputDescriptionRef}
              className={`
                w-full h-full 
                px-1 sm:px-2 
                focus:z-[1]
                ${isDarkMode 
                  ? `${textStyleDarkMode} ${isSelected ? bgStyleDarkModeSelected : bgStyleDarkMode}`
                  : `${textStyle} ${isSelected ? 'bg-yellow-50' : bgStyle}`
                }
              `}
              type='text' 
              disabled={editingEnabled === false}
              placeholder='description'
              value={activity.description}
              onFocus={() => {
                try { setSelectedActivityId(activity.id); } catch(error) {}
                descriptionInputLastFocused.current = true;
              }}
              onChange={(event) => {
                siteboardAction(EDIT_ACTIVITY, {
                  subscriptionId: subscriptionId,
                  activity: {
                    ...activity,
                    description: event.target.value,
                  }
                });
              }}
              onKeyDown={(event) => {
                if(event.key === 'ArrowUp') {
                  event.preventDefault();
                } else if(event.key === 'ArrowDown') {
                  event.preventDefault();
                } else if(event.key === 'Enter') {
                  event.preventDefault();
                }
              }}
            />
          </div>
          <div className={`h-full px-[4px] ${!editingEnabled ? 'hidden' : ' flex'} ${isSelected ? (isDarkMode ? bgStyleDarkModeSelected : 'bg-yellow-50') : ''}`}>
            <button  
              className={`sm:px-[2px]`}
              tabIndex={-1}
              title='Toggle Critical'
              onClick={() => {
                if(!editingEnabled && userCanUnpublishSchedule) {
                  siteboardAction(SET_ALERT, { message: scheduleLockedWarning }); 
                  return;
                }
                siteboardAction(EDIT_ACTIVITY, {
                  subscriptionId: subscriptionId,
                  activity: {
                    ...activity,
                    isHighPriority: !activity.isHighPriority,
                  }
                });
              }}
              onFocus={() => {
                try { setSelectedActivityId(activity.id); } catch(error) {}
              }}
            >
              <CriticalIcon isActive={activity.isHighPriority} color={isDarkMode ? fgColorDarkMode : fgColor} />
            </button>
          </div>
        </div>
        {/* Right columns */}
        <div className={`relative flex ${rowHeight} ${rightTableWidth}`}>
          {/* Active Dates */}
          {datesList.map((date, index) => (
            <div 
              key={index}
              className={
                cellWidth+' border '+(isDarkMode ? cellBorderColorRightDarkMode : cellBorderColor)+' border-r-0 border-t-0 last:border-r select-none ' +
                ((editingEnabled === true && !(date.dateString in holidayDatesMap)) ? ' cursor-pointer ' : ' cursor-not-allowed ') +
                (date.dateString in holidayDatesMap
                  ? 
                    (isDarkMode ? 'bg-black/90' : 'bg-[#3c3c3c]') 
                  : 
                   (date.dateString in activity.activeDates ? (activity.isHighPriority ? 'bg-[#f12035]' : 'bg-[#471cff]') : (date.isWeekend ? (isDarkMode ? 'bg-[#666]' : 'bg-[#a59e9e]') : (isSelected ? (isDarkMode ? bgStyleDarkModeSelected + ' text-white' : 'bg-yellow-50') : (isDarkMode ? bgStyleDarkMode : bgStyle)))
                   ))
              }
              onClick={() => {
                if(!editingEnabled && userCanUnpublishSchedule) {
                  siteboardAction(SET_ALERT, { message: scheduleLockedWarning }); 
                  return;
                }

                if (editingEnabled !== true) return;
                if (date.dateString in holidayDatesMap) return;

                try { setSelectedActivityId(activity.id); } catch(error) {}

                const newActiveDates = {...activity.activeDates};
                if(date.dateString in newActiveDates) {
                  delete newActiveDates[date.dateString];
                } else {
                  newActiveDates[date.dateString] = '';
                }
                siteboardAction(EDIT_ACTIVITY, {
                  subscriptionId: subscriptionId,
                  activity: {
                    ...activity,
                    activeDates: newActiveDates,
                  }
                });
              }}
            >
            </div>
          ))}
        </div>
      </div>
    </div>
  )}, [activity, subscriptionId, siteboardAction, dates, holidayDates, editingEnabled, isSelected, setSelectedActivityId, isDarkMode, userCanUnpublishSchedule, isMousedOver, descriptionInputLastFocused]);

  return (<>{activityJSX}</>);
};

const ActivityLineMulti = ({
  activity,
  subscriptionId,
  siteboardAction,
  setSelectedActivityId,
  isNew = false,
  setNewActivityId,
  isMoved = false,
  setMovedActivityId,
  activityContainer,
  userCanUnpublishSchedule = false,
  dates = '[]', // a JSON array, stringified to minimize re-rendering
  holidayDates = '{}', // a JSON map, stringified to minimize re-rendering
  editingEnabled = false,
  isSelected = false,
  isDarkMode = false,
  descriptionInputLastFocused,
}) => {

  const [isMousedOver, setIsMousedOver] = useState(false);

  const containerRef = useRef(null);
  const inputResponsibleRef = useRef(null);
  const inputDescriptionRef = useRef(null);

  // bring activity into view if it is selected, if it has been moved up/down it can move out of view
  // this gets triggered when the activity line rows are reordered after moving a row up/down
  useEffect(() => {
    // focus input when newly created activity is shown
    if(isNew === true) {
      if(descriptionInputLastFocused.current === true) {
        inputDescriptionRef.current.focus();
      } else {
        inputResponsibleRef.current.focus();
      }
      try { setNewActivityId(null); } catch(error) {} // set the activity as no longer new
      return;
    }

    if (isSelected !== true) return;

    // scroll activity line into view if it is not visible
    try {
      const activityContainerBounds = activityContainer.current.getBoundingClientRect();
      const activityLineBounds = containerRef.current.getBoundingClientRect();
      if(activityLineBounds.bottom > (activityContainerBounds.bottom-10) || activityLineBounds.top < (activityContainerBounds.top+10)) {
        // activity line is not fully visible
        containerRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    } catch(error) {}

    if(isMoved) {
      try { setMovedActivityId(null); } catch(error) {} // set the activity as no longer newly moved
    }
  }, [isSelected, isNew, isMoved, setNewActivityId, setMovedActivityId, activityContainer, descriptionInputLastFocused]);

  // blur inputs when escape is pressed
  useEffect(() => {
    const onKeyUp = (event) => {
      if(event.key === 'Escape') { 
        inputResponsibleRef.current.blur();
        inputDescriptionRef.current.blur();
        if(isSelected) {
          setSelectedActivityId(null);
        }
      }
    };
    const onKeyDown = (event) => {
    };

    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('keydown', onKeyDown);
    return () => { // remove listener when component unloads
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('keydown', onKeyDown); 
    };
  }, [setSelectedActivityId, isSelected]);

  // Measure a hidden clone because the textarea's scrollHeight cannot report content shorter than its current height.
  function getTextareaContentHeight(element) {
    if (!element) return 0;

    const styles = window.getComputedStyle(element);
    const clone = element.cloneNode(false);

    clone.removeAttribute("id");
    clone.removeAttribute("name");
    clone.setAttribute("aria-hidden", "true");
    clone.tabIndex = -1;

    // Preserve font, padding, wrapping, and other layout styles.
    for (let index = 0; index < styles.length; index += 1) {
      const property = styles.item(index);
      clone.style.setProperty(property, styles.getPropertyValue(property));
    }

    clone.value = element.value;
    clone.rows = 1;

    Object.assign(clone.style, {
      position: "fixed",
      top: "0",
      left: "-10000px",
      visibility: "hidden",
      pointerEvents: "none",
      //boxSizing: "border-box",
      width: styles.width,
      height: "0px",
      minHeight: "0px",
      maxHeight: "none",
      overflow: "auto",
      transition: "none",
      animation: "none",
    });

    document.body.appendChild(clone);
    const height = clone.scrollHeight;
    clone.remove();

    return height;
  }

  const updateInputHeights = useCallback(() => {
    const inputResponsible = inputResponsibleRef.current;
    const inputDescription = inputDescriptionRef.current;
    if (!inputResponsible || !inputDescription) return;

    // method 1 for computing height, doesn't work as good as rendering offscreen
    // need to set to auto so the scrollHeight will be updated if the textarea content shrinks
    //inputResponsible.style.height = 'auto';
    //inputDescription.style.height = 'auto';
    //const inputHeight = Math.max(inputResponsible.scrollHeight, inputDescription.scrollHeight);
    //inputResponsible.style.height = `${inputHeight}px`;
    //inputDescription.style.height = `${inputHeight}px`;

    const inputHeight = Math.max(getTextareaContentHeight(inputResponsible), getTextareaContentHeight(inputDescription));
    inputResponsible.style.height = `${inputHeight}px`;
    inputDescription.style.height = `${inputHeight}px`;

    // scroll activity line into view if it is not visible
    try {
      if(!isSelected) throw new Error();
      const activityContainerBounds = activityContainer.current.getBoundingClientRect();
      const activityLineBounds = containerRef.current.getBoundingClientRect();
      if(activityLineBounds.bottom > (activityContainerBounds.bottom-10) || activityLineBounds.top < (activityContainerBounds.top+10)) {
        // activity line is not fully visible
        containerRef.current.scrollIntoView({
          behavior: 'instant',
          block: 'center',
        });
      }
    } catch(error) {}
  }, [isSelected, activityContainer]);

  useEffect(() => {
    window.addEventListener("resize", updateInputHeights);
    return () => {
      window.removeEventListener("resize", updateInputHeights);
    };
  }, [updateInputHeights]);

  useLayoutEffect(() => {
    updateInputHeights();
  }, [activity.responsible, activity.description, updateInputHeights]);

  const activityJSX = useMemo(() => {
    const datesList = JSON.parse(dates);
    const holidayDatesMap = JSON.parse(holidayDates);

    return (
    <div ref={containerRef} className='group relative overflow-visible'>
      <div  
        className={'flex w-full break-inside-avoid activityLine ' + (isSelected ? '' : '')}
        onMouseEnter={() => { setIsMousedOver(true); }}
        onMouseLeave={() => { setIsMousedOver(false); }}
      >
        {/* Highlight mouse-over overlay */}
        {(isMousedOver && !isSelected) && 
          <div className={`absolute z-[2] left-0 top-0 w-full h-full pointer-events-none ${isDarkMode ? 'bg-neutral-400 opacity-20' : 'bg-neutral-400 opacity-20'}`}></div>
        }
        {/* Left columns */}
        <div className={`flex items-start ${leftTableWidth}`}>
          {/* Responsible */}
          <div className={`flex items-start border ${isDarkMode ? cellBorderColorDarkMode : cellBorderLeftColor} border-r-0 border-t-0 group-first:border-t ${responsibleColWidth}`}>
            <textarea
              ref={inputResponsibleRef}
              className={`
                w-full h-full 
                px-1 sm:px-2 py-0.5
                focus:z-[1]
                resize-none overflow-hidden
                ${isDarkMode 
                  ? `${textStyleDarkMode} ${isSelected ? bgStyleDarkModeSelected : bgStyleDarkMode}`
                  : `${textStyle} ${isSelected ? 'bg-yellow-50' : bgStyle}`
                }
              `}
              rows={1}
              type='text' 
              placeholder='responsible'
              disabled={editingEnabled === false}
              value={activity.responsible}
              onFocus={() => {
                try { setSelectedActivityId(activity.id); } catch(error) {} 
                descriptionInputLastFocused.current = false;
              }}
              onChange={(event) => {
                siteboardAction(EDIT_ACTIVITY, {
                  subscriptionId: subscriptionId,
                  activity: {
                    ...activity,
                    responsible: event.target.value,
                  }
                });
              }}
              onKeyDown={(event) => {
                if(event.key === 'ArrowUp') {
                  event.preventDefault();
                } else if(event.key === 'ArrowDown') {
                  event.preventDefault();
                } else if(event.key === 'Enter') {
                  event.preventDefault();
                }
              }}
            />
          </div>
          {/* Description */}
          <div className={`flex items-start border ${isDarkMode ? cellBorderColorDarkMode : cellBorderLeftColor} border-t-0 group-first:border-t ${descriptionColWidth} border-r-0 ${(editingEnabled) && 'border-r-[1px]'}`}>
            <textarea 
              ref={inputDescriptionRef}
              className={`
                w-full h-full 
                px-1 sm:px-2 py-0.5
                focus:z-[1]
                resize-none overflow-hidden
                ${isDarkMode 
                  ? `${textStyleDarkMode} ${isSelected ? bgStyleDarkModeSelected : bgStyleDarkMode}`
                  : `${textStyle} ${isSelected ? 'bg-yellow-50' : bgStyle}`
                }
              `}
              rows={1}
              type='text' 
              disabled={editingEnabled === false}
              placeholder='description'
              value={activity.description}
              onFocus={() => {
                try { setSelectedActivityId(activity.id); } catch(error) {}
                descriptionInputLastFocused.current = true;
              }}
              onChange={(event) => {
                siteboardAction(EDIT_ACTIVITY, {
                  subscriptionId: subscriptionId,
                  activity: {
                    ...activity,
                    description: event.target.value,
                  }
                });
              }}
              onKeyDown={(event) => {
                if(event.key === 'ArrowUp') {
                  event.preventDefault();
                } else if(event.key === 'ArrowDown') {
                  event.preventDefault();
                } else if(event.key === 'Enter') {
                  event.preventDefault();
                }
              }}
            />
          </div>
          <div className={`h-full px-[4px] ${!editingEnabled ? 'hidden' : ' flex'} ${isSelected ? (isDarkMode ? bgStyleDarkModeSelected : 'bg-yellow-50') : ''}`}>
            <button  
              className={`sm:px-[2px]`}
              tabIndex={-1}
              title='Toggle Critical'
              onClick={() => {
                if(!editingEnabled && userCanUnpublishSchedule) {
                  siteboardAction(SET_ALERT, { message: scheduleLockedWarning }); 
                  return;
                }
                siteboardAction(EDIT_ACTIVITY, {
                  subscriptionId: subscriptionId,
                  activity: {
                    ...activity,
                    isHighPriority: !activity.isHighPriority,
                  }
                });
              }}
              onFocus={() => {
                try { setSelectedActivityId(activity.id); } catch(error) {}
              }}
            >
              <CriticalIcon isActive={activity.isHighPriority} color={isDarkMode ? fgColorDarkMode : fgColor} />
            </button>
          </div>
        </div>
        {/* Right columns */}
        <div className={`relative flex ${rightTableWidth}`}>
          {/* Active Dates */}
          {datesList.map((date, index) => (
            <div 
              key={index}
              className={
                cellWidth+' border '+(isDarkMode ? cellBorderColorRightDarkMode : cellBorderColor)+' border-r-0 border-t-0 last:border-r select-none ' +
                ((editingEnabled === true && !(date.dateString in holidayDatesMap)) ? ' cursor-pointer ' : ' cursor-not-allowed ') +
                (date.dateString in holidayDatesMap
                  ? 
                    (isDarkMode ? 'bg-black/90' : 'bg-[#3c3c3c]') 
                  : 
                   (date.dateString in activity.activeDates ? (activity.isHighPriority ? 'bg-[#f12035]' : 'bg-[#471cff]') : (date.isWeekend ? (isDarkMode ? 'bg-[#666]' : 'bg-[#a59e9e]') : (isSelected ? (isDarkMode ? bgStyleDarkModeSelected + ' text-white' : 'bg-yellow-50') : (isDarkMode ? bgStyleDarkMode : bgStyle))))
                )
              }
              onClick={() => {
                if(!editingEnabled && userCanUnpublishSchedule) {
                  siteboardAction(SET_ALERT, { message: scheduleLockedWarning }); 
                  return;
                }

                if (editingEnabled !== true) return;
                if (date.dateString in holidayDatesMap) return;

                try { setSelectedActivityId(activity.id); } catch(error) {}

                const newActiveDates = {...activity.activeDates};
                if(date.dateString in newActiveDates) {
                  delete newActiveDates[date.dateString];
                } else {
                  newActiveDates[date.dateString] = '';
                }
                siteboardAction(EDIT_ACTIVITY, {
                  subscriptionId: subscriptionId,
                  activity: {
                    ...activity,
                    activeDates: newActiveDates,
                  }
                });
              }}
            >
            </div>
          ))}
        </div>
      </div>
    </div>
  )}, [activity, subscriptionId, siteboardAction, dates, holidayDates, editingEnabled, isSelected, setSelectedActivityId, isDarkMode, userCanUnpublishSchedule, isMousedOver, descriptionInputLastFocused]);

  return (<>{activityJSX}</>);
};

const mapStateToProps = state => { return { // define redux state that will be passed in as props
  _projects: state.serverSynced.projects,
  _schedules: state.serverSynced.schedules,
  _activities: state.serverSynced.activities,
  _subscriptions: state.serverSynced.subscriptions,
  _userId: state.serverSynced.userId,
  _users: state.serverSynced.users,
  _userPreferences: state.serverSynced.userPreferences,
  _unreadScheduleMessageThreads: state.serverSynced.unreadScheduleMessageThreads,
  _scheduleMessageThreads: state.serverSynced.scheduleMessageThreads,
  _isTouchDevice: state.localEphemeral.isTouchDevice,
  _localOnlyMode: state.serverSynced.localOnlyMode,
  _isUsingShareScheduleLink: state.localEphemeral.isUsingShareScheduleLink,
  _contactSharedScheduleStats: state.serverSynced.contactSharedScheduleStats,
}};
export default connect(mapStateToProps, {setPath, setPublishScheduleDialogueId, setUnpublishScheduleDialogueId, setDeleteScheduleDialogueId, setNewScheduleDialogueId, siteboardAction })(Schedule);
